import type { AgingBucket, RiskLevel } from "./contasReceberApi";

// Estado da fatura → cor (apenas classes presentes no index.css pré-compilado).
const STATUS_CLASS: Record<string, string> = {
  rascunho: "bg-muted text-secondary-text",
  emitida: "bg-chart-1/10 text-chart-1",
  parcialmente_paga: "bg-yellow-500/10 text-yellow-700",
  paga: "bg-green-500/10 text-green-700",
  anulada: "bg-muted text-muted-foreground",
};

export function StatusBadge({
  code,
  name,
}: {
  code: string;
  name: string;
}) {
  const cls = STATUS_CLASS[code] ?? "bg-muted text-secondary-text";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 ${cls}`}
      style={{
        fontSize: "var(--text-xs)",
        borderRadius: "var(--radius)",
        fontWeight: "var(--font-weight-medium)",
      }}
    >
      {name}
    </span>
  );
}

// Antiguidade (verde → amarelo → vermelho), sem cores ausentes do index.css.
const AGING_META: Record<AgingBucket, { label: string; cls: string }> = {
  current: { label: "Corrente", cls: "bg-green-500/10 text-green-700" },
  d0_30: { label: "0-30 dias", cls: "bg-green-500/10 text-green-600" },
  d31_60: { label: "31-60 dias", cls: "bg-yellow-500/10 text-yellow-700" },
  d61_90: { label: "61-90 dias", cls: "bg-red-500/10 text-red-600" },
  d90_plus: { label: "+90 dias", cls: "bg-red-500/10 text-red-700" },
};

export function AgingBadge({
  bucket,
  ageDays,
}: {
  bucket?: AgingBucket | null;
  ageDays?: number | null;
}) {
  if (!bucket) return <span className="text-secondary-text">—</span>;
  const meta = AGING_META[bucket];
  const suffix =
    ageDays !== null && ageDays !== undefined && Number.isFinite(ageDays)
      ? ` · ${ageDays}d`
      : "";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 ${meta.cls}`}
      style={{
        fontSize: "var(--text-xs)",
        borderRadius: "var(--radius)",
        fontWeight: "var(--font-weight-medium)",
      }}
      title={`Antiguidade: ${meta.label}`}
    >
      {meta.label}
      {suffix}
    </span>
  );
}

// Risco de crédito do cliente (verde → amarelo → vermelho), só classes do index.css.
const RISK_META: Record<RiskLevel, { label: string; cls: string }> = {
  green: { label: "Risco baixo", cls: "bg-green-500/10 text-green-700" },
  yellow: { label: "Risco médio", cls: "bg-yellow-500/10 text-yellow-700" },
  red: { label: "Risco alto", cls: "bg-red-500/10 text-red-700" },
};

export function RiskBadge({
  level,
  title,
}: {
  level?: RiskLevel | null;
  title?: string;
}) {
  if (!level) return <span className="text-secondary-text">—</span>;
  const meta = RISK_META[level];
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 ${meta.cls}`}
      style={{
        fontSize: "var(--text-xs)",
        borderRadius: "var(--radius)",
        fontWeight: "var(--font-weight-medium)",
      }}
      title={title ?? meta.label}
    >
      {meta.label}
    </span>
  );
}

export function KindBadge({ kind }: { kind: "invoice" | "credit_note" }) {
  if (kind === "invoice") return null;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 bg-chart-2/10 text-chart-2"
      style={{
        fontSize: "var(--text-xs)",
        borderRadius: "var(--radius)",
        fontWeight: "var(--font-weight-medium)",
      }}
    >
      NC
    </span>
  );
}
